/**
 * Utility functions for handling API errors and ApiResponse structures
 */

export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: string[];
  statusCode: number;
}

/**
 * Extracts error messages from various error structures
 */
export const extractErrorMessage = (error: any): string => {
  console.log("Error structure:", error);
  
  // Handle ApiResponse structure with Errors list
  if (error?.data?.errors && Array.isArray(error.data.errors) && error.data.errors.length > 0) {
    return error.data.errors.join(', ');
  }
  
  // Handle ApiResponse message
  if (error?.data?.message) {
    return error.data.message;
  }
  
  // Handle standard error properties
  if (error?.message) {
    return error.message;
  }
  
  // Handle HTTP status codes
  if (error?.status === 400) {
    return "Invalid data provided. Please check your input and try again.";
  }
  if (error?.status === 404) {
    return "Resource not found. It may have been deleted.";
  }
  if (error?.status === 403) {
    return "You don't have permission to perform this action.";
  }
  if (error?.status === 409) {
    return "A resource with this information already exists.";
  }
  if (error?.status >= 500) {
    return "Server error occurred. Please try again later.";
  }
  
  // Fallback for unknown error structures
  if (typeof error === 'string') {
    return error;
  }
  
  return "An unexpected error occurred. Please try again.";
};

/**
 * Extracts all error messages from ApiResponse errors list
 */
export const extractAllErrors = (error: any): string[] => {
  if (error?.data?.errors && Array.isArray(error.data.errors)) {
    return error.data.errors;
  }
  
  const message = extractErrorMessage(error);
  return message ? [message] : [];
};

/**
 * Checks if the error is a validation error (400 status with errors list)
 */
export const isValidationError = (error: any): boolean => {
  return error?.status === 400 && 
         error?.data?.errors && 
         Array.isArray(error.data.errors) && 
         error.data.errors.length > 0;
};

/**
 * Checks if the error is a conflict error (409 status)
 */
export const isConflictError = (error: any): boolean => {
  return error?.status === 409;
};

/**
 * Checks if the error is a not found error (404 status)
 */
export const isNotFoundError = (error: any): boolean => {
  return error?.status === 404;
};



